import React from 'react';
import PropTypes from 'prop-types';

const BasicInfoForm = ({ formData, errors, onChange, disabled = false }) => {
  const handleInputChange = (field, value) => {
    onChange(field, value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // Extract YYYY-MM-DD part
  };

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6 space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Basic Information
      </h2>

      {/* Provider Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Provider Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Provider Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Provider Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
              placeholder="Enter provider name"
              value={formData.providerName || ''}
              onChange={(e) => handleInputChange('providerName', e.target.value)}
              disabled={disabled}
            />
            {errors.providerName && (
              <p className="text-red-500 text-sm mt-1">{errors.providerName}</p>
            )}
          </div>

          {/* Provider Contact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Provider Contact <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
              placeholder="+91-1234567890"
              value={formData.providerContact || ''}
              onChange={(e) => handleInputChange('providerContact', e.target.value)}
              disabled={disabled}
            />
            {errors.providerContact && (
              <p className="text-red-500 text-sm mt-1">{errors.providerContact}</p>
            )}
          </div>

          {/* Provider Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Provider Email
            </label>
            <input
              type="email"
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
              placeholder="provider@example.com"
              value={formData.providerEmail || ''}
              onChange={(e) => handleInputChange('providerEmail', e.target.value)}
              disabled={disabled}
            />
            {errors.providerEmail && (
              <p className="text-red-500 text-sm mt-1">{errors.providerEmail}</p>
            )}
          </div>

          {/* Provider Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Provider Website
            </label>
            <input
              type="url"
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
              placeholder="https://www.example.com"
              value={formData.providerWebsite || ''}
              onChange={(e) => handleInputChange('providerWebsite', e.target.value)}
              disabled={disabled}
            />
            {errors.providerWebsite && (
              <p className="text-red-500 text-sm mt-1">{errors.providerWebsite}</p>
            )}
          </div>
        </div>
      </div>

      {/* Date Range and Billing */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Scheme Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
              value={formatDate(formData.startDate)}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              disabled={disabled}
            />
            {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
              value={formatDate(formData.expiryDate)}
              onChange={(e) => handleInputChange('expiryDate', e.target.value)}
              disabled={disabled}
              min={formatDate(formData.startDate)}
            />
            {errors.expiryDate && <p className="text-red-500 text-sm mt-1">{errors.expiryDate}</p>}
          </div>

          {/* Billing Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Billing Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                £
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full rounded-lg px-4 py-2 pl-8 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
                placeholder="0.00"
                value={formData.billingAmount || ''}
                onChange={(e) =>
                  handleInputChange('billingAmount', parseFloat(e.target.value) || 0)
                }
                disabled={disabled}
              />
            </div>
            {errors.billingAmount && (
              <p className="text-red-500 text-sm mt-1">{errors.billingAmount}</p>
            )}
          </div>

          {/* No of Seats */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              No of Seats <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
              placeholder="Enter number of seats"
              value={formData.seats || ''}
              onChange={(e) => handleInputChange('seats', parseInt(e.target.value) || 0)}
              disabled={disabled}
            />
            {errors.seats && <p className="text-red-500 text-sm mt-1">{errors.seats}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

BasicInfoForm.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

BasicInfoForm.defaultProps = {
  errors: {},
  disabled: false
};

export default BasicInfoForm;
